import type { FormField } from '../../types';
import type { CampaignExportFilters } from '../campaign-csv';
import { buildQuestionSet, filterPopulation, observeQuestions, type AnalyticsQuestion, type AnalyticsResponse, type Observation } from './normalize';
import { correlationMatrix, encodeQuestion, suggestPairs, type CorrelationCell, type EncodedQuestion, type SuggestedPair } from './compare';
import { buildDropOff, type DropOff } from './funnel';
import { median } from './stats';
import { summarizeQuestion, type QuestionSummary } from './summarize';

/** At most four headline numbers. */
export interface Headline {
  responses: number;
  completed: number;
  /** Completed share of all responses, or null without responses. */
  completionRate: number | null;
  /** Median minutes from start to completion over completed responses. */
  medianMinutes: number | null;
}

/** Cards between two information blocks, in schema order. */
export interface OverviewSection {
  /** First non-empty label of the information blocks that open the section. */
  heading: string | null;
  /** Display name: the heading, else the section's first question. */
  label: string;
  /** Chart cards and one-line rows in schema order. */
  items: QuestionSummary[];
  /** Text questions listed with their answered counts at the end of the section. */
  texts: QuestionSummary[];
}

/** Numeric relationships: suggested pairs and, with 4+ numeric questions, the full matrix. */
export interface Relationships {
  numeric: EncodedQuestion[];
  suggested: SuggestedPair[];
  matrix: CorrelationCell[] | null;
}

/** Everything the Overview renders, computed from stored answers only. */
export interface CampaignOverviewModel {
  headline: Headline;
  /** Up to 6 chart cards to lead with: numbers and clear majorities, in schema order. */
  highlights: QuestionSummary[];
  population: AnalyticsResponse[];
  questions: AnalyticsQuestion[];
  sections: OverviewSection[];
  /** Comparison encodings by field id. */
  encoded: Map<string, EncodedQuestion>;
  relationships: Relationships;
  dropOff: DropOff;
}

/** Injectable hooks; the summarizer is replaceable for fault tests. */
export interface BuildOverviewOptions {
  onError?: (error: unknown, question: AnalyticsQuestion) => void;
  summarize?: typeof summarizeQuestion;
}

/** At most this many highlight cards. */
const MAX_HIGHLIGHTS = 6;
/** A distribution is a highlight when its top answer holds at least this share. */
const HIGHLIGHT_SHARE = 0.5;

/** The relationships card appears with this many numeric questions. */
const MIN_RELATIONSHIP_QUESTIONS = 2;
/** The full matrix appears with this many numeric questions. */
const MIN_MATRIX_QUESTIONS = 4;
/** Statuses that mean a conversation has not finished. */
const UNFINISHED = new Set(['pending', 'in_progress', 'abandoned']);

/** Responses, completions, completion rate and median minutes to complete. */
export function buildHeadline(responses: readonly AnalyticsResponse[]): Headline {
  const completed = responses.filter(row => row.response_status === 'completed');
  const minutes = completed.flatMap(row => {
    if (!row.completed_at) return [];
    const duration = (Date.parse(row.completed_at) - Date.parse(row.created_at)) / 60_000;
    return Number.isFinite(duration) && duration >= 0 ? [duration] : [];
  });
  return {
    responses: responses.length,
    completed: completed.length,
    completionRate: responses.length ? completed.length / responses.length : null,
    medianMinutes: median(minutes),
  };
}

/**
 * Group summaries into sections that start at each information block of the
 * current schema. Consecutive blocks merge, the heading is their first
 * non-empty label (else the section is named after its first question), and
 * empty sections are dropped. Snapshot-only questions join the last section.
 */
export function buildSections(fields: FormField[], summaries: readonly QuestionSummary[]): OverviewSection[] {
  const sections: OverviewSection[] = [{ heading: null, label: '', items: [], texts: [] }];
  const sectionOf = new Map<string, OverviewSection>();
  let openedByBlock = false;
  for (const item of fields) {
    if (item.type === 'information') {
      if (!openedByBlock) sections.push({ heading: null, label: '', items: [], texts: [] });
      const current = sections[sections.length - 1];
      if (!current.heading && item.label.trim()) current.heading = item.label.trim();
      openedByBlock = true;
      continue;
    }
    openedByBlock = false;
    sectionOf.set(item.id, sections[sections.length - 1]);
  }
  // Summaries arrive in page order, so the first one a section receives is its first question.
  const firstLabel = new Map<OverviewSection, string>();
  for (const summary of summaries) {
    const section = sectionOf.get(summary.fieldId) ?? sections[sections.length - 1];
    (summary.display === 'text' ? section.texts : section.items).push(summary);
    if (!firstLabel.has(section)) firstLabel.set(section, summary.label);
  }
  return sections
    .filter(section => section.items.length || section.texts.length)
    .map(section => ({ ...section, label: section.heading ?? firstLabel.get(section) ?? '' }));
}

/**
 * The cards to lead with: number questions and distributions whose top answer
 * holds at least half of the answers, in schema order, at most 6.
 */
export function selectHighlights(summaries: readonly QuestionSummary[]): QuestionSummary[] {
  return summaries.filter(summary => summary.display === 'chart' && (
    summary.kind === 'numeric' || summary.kind === 'nps'
    || (summary.kind === 'distribution' && summary.items.some(item => item.share >= HIGHLIGHT_SHARE))
  )).slice(0, MAX_HIGHLIGHTS);
}

/** Count-only fallback when a summarizer fails unexpectedly. */
function fallbackSummary(question: AnalyticsQuestion, observations: readonly Observation[]): QuestionSummary {
  return {
    kind: 'count',
    display: 'line',
    fieldId: question.id,
    fieldType: question.type,
    label: question.label,
    answered: observations.filter(entry => entry.status === 'answered').length,
    eligible: observations.length,
    excluded: observations.filter(entry => entry.status === 'excluded').length,
  };
}

/**
 * Build the Overview: filter the population with the export rules, summarize
 * every question, chunk by information blocks, and add relationships and
 * drop-off. Pure: same inputs always give the same numbers.
 */
export function buildOverview(fields: FormField[], responses: readonly AnalyticsResponse[], filters: CampaignExportFilters, options: BuildOverviewOptions = {}): CampaignOverviewModel {
  const summarize = options.summarize ?? summarizeQuestion;
  const population = filterPopulation(responses, fields, filters);
  const unfiltered = filterPopulation(responses, fields, { ...filters, status: undefined });
  // Columns come from the filtered responses, exactly as the CSV export builds them.
  const questions = buildQuestionSet(fields, population);
  const observations = observeQuestions(questions, population, fields);
  const encoded = new Map<string, EncodedQuestion>();
  const summaries = questions.map(question => {
    const list = observations.get(question.id)!;
    let summary: QuestionSummary;
    try {
      summary = summarize(question, list);
    } catch (error) {
      options.onError?.(error, question);
      return fallbackSummary(question, list);
    }
    const encoding = encodeQuestion(question, list, summary);
    if (encoding && summary.display === 'chart') encoded.set(question.id, encoding);
    return summary;
  });
  const numeric = [...encoded.values()].filter(item => item.encoding.type === 'numeric');
  const populationIds = new Set(population.map(row => row.id));
  const dropOffRows = [...population, ...unfiltered.filter(row => !populationIds.has(row.id) && UNFINISHED.has(row.response_status ?? 'in_progress'))];
  return {
    headline: buildHeadline(unfiltered),
    highlights: selectHighlights(summaries),
    population,
    questions,
    sections: buildSections(fields, summaries),
    encoded,
    relationships: {
      numeric,
      suggested: numeric.length >= MIN_RELATIONSHIP_QUESTIONS ? suggestPairs(numeric) : [],
      matrix: numeric.length >= MIN_MATRIX_QUESTIONS ? correlationMatrix(numeric).cells : null,
    },
    dropOff: buildDropOff(fields, dropOffRows),
  };
}
